import { useCallback, useEffect, useMemo, useRef } from "react";

import { useLazyGetCommsTextTemplatesQuery } from "services/suno-comms/suno-comms-api";
import { TemplateSourceType } from "services/suno-comms/types/text-templates.type";
import { useLazyGetTextTemplatesQuery } from "services/text-templates";
import type { TextTemplateListItem } from "services/types/text-templates.type";

import { commsTemplateToTextTemplate } from "./text-templates-v2/text-templates-v2.utils";

const PROJECTION = "{id,name,type,category}";
export const TEMPLATE_SEARCH_PAGE_SIZE = 25;
// Matches the full template picker's debounce (ENG-1426).
export const TEMPLATE_SEARCH_DEBOUNCE_MS = 250;

export type TemplatePage = {
  templates: TextTemplateListItem[];
  // A full page implies there may be more; the next page settles it (the
  // backend uses count-less pagination, so we infer "more" from page fullness).
  hasMore: boolean;
};

export type TemplateSearcher = {
  // First page for a new term (offset 0), debounced so typing doesn't fire one
  // request per keystroke.
  searchFirstPage: (search: string) => Promise<TemplatePage>;
  // Subsequent pages for "Show more" (offset > 0), fired immediately.
  fetchPage: (search: string, offset: number) => Promise<TemplatePage>;
};

/**
 * Server-side, paginated template search for the inline "/" word processor
 * autocompleter. The pre-loaded browse list is capped at the backend's 300-row
 * limit, so search must hit the backend to reach templates beyond that cap, and
 * "Show more" pages through the rest instead of truncating to a single page
 * (ENG-1426). `templateTypes` and `templateSource` are read via refs so the
 * returned searcher stays referentially stable and does not re-run the editor
 * setup on every render.
 */
export const useTemplateSearch = (
  templateTypes?: number[],
  templateSource: TemplateSourceType = "default"
): TemplateSearcher => {
  const [fetchTextTemplates] = useLazyGetTextTemplatesQuery();
  const [fetchCommsTextTemplates] = useLazyGetCommsTextTemplatesQuery();

  const templateTypesRef = useRef(templateTypes);
  templateTypesRef.current = templateTypes;

  const templateSourceRef = useRef(templateSource);
  templateSourceRef.current = templateSource;

  const fetchPage = useCallback(
    async (search: string, offset: number): Promise<TemplatePage> => {
      const types = templateTypesRef.current;
      const isLeads = templateSourceRef.current === "leads";
      const typeFilter = types?.length ? { type: types } : {};
      try {
        const defaultRows =
          (await fetchTextTemplates({
            query: PROJECTION,
            search,
            limit: TEMPLATE_SEARCH_PAGE_SIZE,
            offset,
            ...typeFilter
          }).unwrap()) ?? [];

        if (!isLeads) {
          return {
            templates: defaultRows,
            hasMore: defaultRows.length === TEMPLATE_SEARCH_PAGE_SIZE
          };
        }

        // Leads surfaces merge the native Comms templates with the customer's
        // main-app templates so both are searchable inline (SN-18785).
        const commsRows =
          (await fetchCommsTextTemplates({
            search,
            limit: TEMPLATE_SEARCH_PAGE_SIZE,
            offset,
            ...typeFilter
          }).unwrap()) ?? [];
        return {
          templates: [
            ...commsRows.map(commsTemplateToTextTemplate),
            ...defaultRows
          ],
          hasMore:
            commsRows.length === TEMPLATE_SEARCH_PAGE_SIZE ||
            defaultRows.length === TEMPLATE_SEARCH_PAGE_SIZE
        };
      } catch {
        return { templates: [], hasMore: false };
      }
    },
    [fetchTextTemplates, fetchCommsTextTemplates]
  );

  // Debounce only the keystroke-driven first page. A burst of keystrokes
  // collapses into a single request for the latest term; every pending caller
  // resolves with that page.
  const debounceRef = useRef<{
    timer?: ReturnType<typeof setTimeout>;
    resolvers: ((page: TemplatePage) => void)[];
  }>({ resolvers: [] });

  useEffect(
    () => () => {
      if (debounceRef.current.timer) {
        clearTimeout(debounceRef.current.timer);
      }
    },
    []
  );

  const searchFirstPage = useCallback(
    (search: string): Promise<TemplatePage> =>
      new Promise((resolve) => {
        const state = debounceRef.current;
        state.resolvers.push(resolve);
        if (state.timer) {
          clearTimeout(state.timer);
        }
        state.timer = setTimeout(async () => {
          const resolvers = state.resolvers;
          state.resolvers = [];
          const page = await fetchPage(search, 0);
          resolvers.forEach((resolveOne) => resolveOne(page));
        }, TEMPLATE_SEARCH_DEBOUNCE_MS);
      }),
    [fetchPage]
  );

  return useMemo(
    () => ({ searchFirstPage, fetchPage }),
    [searchFirstPage, fetchPage]
  );
};
